import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  inject,
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  FormControl,
  ReactiveFormsModule,
  FormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { I18N_FACADE, I18nFacade } from '@angular-monorepo/i18n';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { Textarea } from 'primeng/textarea';
import { InputNumber } from 'primeng/inputnumber';
import { AutoComplete } from 'primeng/autocomplete';
import { Button } from 'primeng/button';

interface SearchPatientEvent {
  query: string;
}

interface SelectPatientEvent {
  value: Patient;
}

interface ProgramChangeEvent {
  value: string;
}

interface OccupiedRoomObject {
  room_id?: string;
  room?: string;
}

type OccupiedRoom = string | OccupiedRoomObject;

interface AvailabilityResponse {
  occupied_rooms?: OccupiedRoom[];
  data?: {
    occupied_rooms?: OccupiedRoom[];
  };
}

export interface Room {
  room_id: string;
  room_number: string;
  room_type?: string;
  status?: string;
}

export interface Patient {
  patient_id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone_primary?: string;
}

export interface Program {
  program_id: string;
  program_type: string;
  name: string;
  code: string;
  description?: string;
  is_active: boolean;
  tiers: ProgramTier[];
}

export interface ProgramTier {
  program_tier_id: string;
  program: string;
  tier: string;
  is_active: boolean;
  prices: ProgramTierPrice[];
}

export interface ProgramTierPrice {
  program_tier_price_id: string;
  currency: string;
  price: string;
  duration_days: number;
  is_active: boolean;
}

export interface ReservationFormData {
  room: string;
  patient: string; // Patient ID
  check_in_date: string;
  check_out_date: string;
  status: string;
  total_amount: string;
  deposit_amount: string;
  special_requests: string;
  // Campos adicionales para el frontend
  program_id?: string;
  tier_id?: string;
  price_id?: string;
}

export interface RoomUpdateService {
  updateRoom(roomId: string, data: { status: string }): void;
}

// Servicios que deben proporcionarse desde el componente padre
export interface PatientSearchService {
  searchPatients(query: string): Promise<Patient[]>;
}

export interface ProgramService {
  getPrograms(): Promise<Program[]>;
}

export interface ReservationValidationService {
  checkAvailability(checkInDate: string, checkOutDate: string): Promise<AvailabilityResponse>;
}

@Component({
  selector: 'lib-reservation-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    Select,
    DatePicker,
    Textarea,
    InputNumber,
    AutoComplete,
    Button,
  ],
  templateUrl: './reservation-form.component.html',
  styleUrls: ['./reservation-form.component.css'],
})
export class ReservationFormComponent implements OnInit, OnChanges {
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private readonly i18n = inject<I18nFacade>(I18N_FACADE);

  @Input() rooms: Room[] = [];
  @Input() initialData: ReservationFormData | null = null;
  @Input() isEditMode = false;
  @Input() isViewMode = false; // Modo solo lectura
  @Input() roomUpdateService: RoomUpdateService | null = null;
  @Input() patientSearchService: PatientSearchService | null = null;
  @Input() programService: ProgramService | null = null;
  @Input() reservationValidationService: ReservationValidationService | null = null;

  @Output() formSubmit = new EventEmitter<{
    reservationData: ReservationFormData;
    pendingPayment: unknown;
  }>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() registerPayment = new EventEmitter<ReservationFormData>();
  @Output() showMessage = new EventEmitter<{ severity: string; summary: string; detail: string }>();

  reservationForm!: FormGroup;
  patientSearchControl = new FormControl<Patient | null>(null);
  private previousRoomId: string | null = null;

  // Autocomplete pacientes
  filteredPatients: Patient[] = [];
  selectedPatient: Patient | null = null;

  // Programas y planes
  programs: Program[] = [];
  selectedProgram: Program | null = null;
  availablePlans: { duration_days: number; prices: ProgramTierPrice[]; tier: string }[] = [];
  selectedPlan: { duration_days: number; prices: ProgramTierPrice[]; tier: string } | null = null;
  selectedPrice: ProgramTierPrice | null = null;

  // Validación de disponibilidad
  availabilityError: string | null = null;
  isValidatingAvailability = false;

  // Control de cambios en modo edición
  initialStatus: string | null = null;
  statusHasChanged = false;
  initialCheckInDate: string | null = null;
  checkInDateHasChanged = false;

  // Helper para el template
  parseFloat = parseFloat;

  // Estados válidos del backend
  statusOptions = [
    { labelKey: 'reservations.status.tentative', value: 'tentative' },
    { labelKey: 'reservations.status.halfConfirmed', value: 'half_confirmed' },
    { labelKey: 'reservations.status.booked', value: 'booked' },
    { labelKey: 'reservations.status.checkedIn', value: 'checked_in' },
    { labelKey: 'reservations.status.checkedOut', value: 'checked_out' },
    { labelKey: 'reservations.status.noShow', value: 'no_show' },
    { labelKey: 'reservations.status.canceled', value: 'canceled' },
    { labelKey: 'reservations.status.archived', value: 'archived' },
  ];

  // Mapa de días de tratamiento según paquete
  treatmentDayMap: Record<number, number> = {
    7: 4,
    10: 4,
    14: 8,
    35: 28,
  };

  // Fecha mínima para el calendario (hoy)
  minDate = new Date();

  private parseLocalDate(dateString: string): Date {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadPrograms();
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('🔄 ngOnChanges llamado con:', Object.keys(changes));
    console.log('🔄 isEditMode actual:', this.isEditMode);
    console.log('🔄 isViewMode actual:', this.isViewMode);

    // Cargar datos iniciales cuando cambian
    if (changes['initialData'] && this.reservationForm && this.initialData) {
      console.log('📥 ngOnChanges: initialData cambió');
      console.log('📥 initialData recibido:', this.initialData);
      console.log('📥 check_in_date:', this.initialData.check_in_date);
      console.log('📥 check_out_date:', this.initialData.check_out_date);
      // Usar setTimeout para asegurar que isEditMode esté actualizado
      setTimeout(() => {
        console.log(
          '📥 Ejecutando loadInitialData después de timeout, isEditMode:',
          this.isEditMode,
        );
        this.loadInitialData();
        this.cdr.detectChanges();
      }, 0);
    }

    // Deshabilitar/habilitar formulario según el modo
    if (changes['isViewMode'] && this.reservationForm) {
      if (this.isViewMode) {
        this.reservationForm.disable();
        console.log('🔒 Formulario deshabilitado (modo solo lectura)');
      } else {
        this.reservationForm.enable();
        // El campo check_out_date siempre está deshabilitado porque se calcula automáticamente
        this.reservationForm.get('check_out_date')?.disable();
        this.reservationForm.get('total_amount')?.disable();
        console.log('🔓 Formulario habilitado (modo edición/creación)');
      }
    }

    // En modo edición, deshabilitar todos los campos excepto status y check_in_date
    if (changes['isEditMode'] && this.reservationForm) {
      if (this.isEditMode) {
        console.log('✏️ Modo edición: deshabilitando campos excepto status y check_in_date');
        this.reservationForm.get('room')?.disable();
        this.reservationForm.get('patient')?.disable();
        this.reservationForm.get('program')?.disable();
        this.reservationForm.get('plan')?.disable();
        // check_in_date queda habilitado
        this.reservationForm.get('check_out_date')?.disable();
        this.reservationForm.get('total_amount')?.disable();
        this.reservationForm.get('special_requests')?.disable();
        // status queda habilitado

        // Si hay datos iniciales, recargarlos
        if (this.initialData) {
          console.log('📥 Recargando datos iniciales en modo edición');
          this.loadInitialData();
        }
      }
    }
  }

  initializeForm(): void {
    this.reservationForm = this.fb.group({
      room: ['', Validators.required],
      patient: [null, Validators.required],
      program: ['', Validators.required],
      plan: ['', Validators.required], // Este será el duration_days
      check_in_date: [null, Validators.required],
      check_out_date: [{ value: null, disabled: true }],
      status: ['tentative', Validators.required],
      total_amount: [{ value: '', disabled: true }, [Validators.required, Validators.min(0)]],
      special_requests: [''],
    });

    // Escuchar cambios en el plan seleccionado (duration_days)
    this.reservationForm.get('plan')?.valueChanges.subscribe((planKey: string) => {
      this.onPlanChange(planKey);
    });

    // Escuchar cambios en fecha de entrada para calcular salida y validar disponibilidad
    this.reservationForm.get('check_in_date')?.valueChanges.subscribe(() => {
      // En modo edición, recalcular check_out_date manteniendo la duración original
      if (this.isEditMode) {
        this.recalculateCheckOutDateInEditMode();
      } else {
        this.calculateCheckOutDate();
        this.validateRoomAvailability();
      }
    });

    // Escuchar cambios en fecha de salida para validar disponibilidad
    this.reservationForm.get('check_out_date')?.valueChanges.subscribe(() => {
      this.validateRoomAvailability();
    });

    // Escuchar cambios en la selección de habitación
    this.reservationForm.get('room')?.valueChanges.subscribe((roomId: string) => {
      this.onRoomSelectionChange(roomId);
      this.validateRoomAvailability();
    });

    // Escuchar cambios en el status (para modo edición)
    this.reservationForm.get('status')?.valueChanges.subscribe((newStatus: string) => {
      if (this.isEditMode && this.initialStatus) {
        this.statusHasChanged = newStatus !== this.initialStatus;
        console.log('📊 Status cambió:', {
          inicial: this.initialStatus,
          nuevo: newStatus,
          cambió: this.statusHasChanged,
        });
      }
    });

    // Escuchar cambios en la fecha de check-in (para modo edición)
    this.reservationForm.get('check_in_date')?.valueChanges.subscribe((newDate: Date) => {
      if (this.isEditMode && this.initialCheckInDate && newDate) {
        const newDateStr = this.formatDate(newDate);
        this.checkInDateHasChanged = newDateStr !== this.initialCheckInDate;
        console.log('📅 Fecha de check-in cambió:', {
          inicial: this.initialCheckInDate,
          nueva: newDateStr,
          cambió: this.checkInDateHasChanged,
        });
      }
    });

    if (this.initialData) {
      this.loadInitialData();
    }
  }

  async loadPrograms(): Promise<void> {
    if (!this.programService) {
      console.warn('⚠️ ProgramService no proporcionado');
      return;
    }

    try {
      this.programs = await this.programService.getPrograms();
      console.log('✅ Programas cargados:', this.programs);
    } catch (error) {
      console.error('❌ Error cargando programas:', error);
    }
  }

  // Búsqueda de pacientes (AutoComplete)
  async searchPatient(event: SearchPatientEvent): Promise<void> {
    const query = event.query;

    if (!this.patientSearchService) {
      console.warn('⚠️ PatientSearchService no proporcionado');
      this.filteredPatients = [];
      return;
    }

    if (query.length < 2) {
      this.filteredPatients = [];
      return;
    }

    try {
      this.filteredPatients = await this.patientSearchService.searchPatients(query);
    } catch (error) {
      console.error('❌ Error buscando pacientes:', error);
      this.filteredPatients = [];
    }
  }

  onPatientSelect(event: SelectPatientEvent): void {
    const patient = event.value;
    this.selectedPatient = patient;
    this.reservationForm.patchValue({
      patient: patient.patient_id,
    });
    console.log('✅ Paciente seleccionado:', patient);
  }

  onPatientClear(): void {
    this.selectedPatient = null;
    this.reservationForm.patchValue({
      patient: null,
    });
  }

  getPatientLabel(patient: Patient): string {
    return `${patient.first_name} ${patient.last_name}${
      patient.email ? ` (${patient.email})` : ''
    }`;
  }

  // Arrow function for optionLabel binding - esto asegura que PrimeNG pueda convertir el objeto a string
  getPatientOptionLabel = (patient: Patient | null): string => {
    if (!patient) return '';
    return `${patient.first_name || ''} ${patient.last_name || ''}`.trim();
  };

  // Selección en cascada: Programa -> Tier -> Precio
  get programOptions() {
    return this.programs
      .filter((p) => p.is_active)
      .map((p) => ({
        label: `${p.program_type} - ${p.name}`,
        value: p.program_id,
        program: p,
      }));
  }

  onProgramChange(event: ProgramChangeEvent): void {
    const programId = event.value;
    this.selectedProgram = this.programs.find((p) => p.program_id === programId) || null;

    if (this.selectedProgram) {
      // Agrupar planes por duration_days de todos los tiers
      const plansMap = new Map<number, { prices: ProgramTierPrice[]; tier: string }>();

      this.selectedProgram.tiers.forEach((tier) => {
        if (tier.is_active) {
          tier.prices.forEach((price) => {
            if (price.is_active) {
              if (!plansMap.has(price.duration_days)) {
                plansMap.set(price.duration_days, { prices: [], tier: tier.tier });
              }
              const groupedPlan = plansMap.get(price.duration_days);
              if (groupedPlan) {
                groupedPlan.prices.push(price);
              }
            }
          });
        }
      });

      this.availablePlans = Array.from(plansMap.entries())
        .map(([duration_days, data]) => ({
          duration_days,
          prices: data.prices,
          tier: data.tier,
        }))
        .sort((a, b) => a.duration_days - b.duration_days);

      this.selectedPlan = null;
      this.selectedPrice = null;

      this.reservationForm.patchValue({
        plan: '',
        total_amount: '',
      });

      console.log('✅ Programa seleccionado:', this.selectedProgram);
      console.log('📋 Planes disponibles:', this.availablePlans);
    }
  }

  get planOptions() {
    return this.availablePlans.map((plan) => ({
      label: `${plan.duration_days} días`,
      value: `${plan.duration_days}`,
      plan: plan,
    }));
  }

  onPlanChange(planKey: string): void {
    const durationDays = parseInt(planKey);
    this.selectedPlan = this.availablePlans.find((p) => p.duration_days === durationDays) || null;

    if (this.selectedPlan && this.selectedPlan.prices.length > 0) {
      // Tomar el primer precio disponible (o podrías mostrar otro dropdown si hay múltiples precios por tier)
      this.selectedPrice = this.selectedPlan.prices[0];

      // Actualizar automáticamente el total_amount y calcular check_out_date
      this.reservationForm.patchValue({
        total_amount: parseFloat(this.selectedPrice.price),
      });

      this.calculateCheckOutDate();

      console.log('✅ Plan seleccionado:', this.selectedPlan);
      console.log('✅ Precio aplicado:', this.selectedPrice);
    }
  }

  onRoomSelectionChange(roomId: string): void {
    if (!roomId || this.isEditMode) {
      return;
    }

    if (this.roomUpdateService) {
      console.log('🏨 Habitación seleccionada:', roomId);
      this.roomUpdateService.updateRoom(roomId, { status: 'occupied' });
      this.previousRoomId = roomId;
    }
  }

  loadInitialData(): void {
    console.log('🔄 loadInitialData() llamado');
    console.log('🔄 this.initialData:', this.initialData);

    if (!this.initialData) {
      console.log('❌ initialData es null/undefined, saliendo');
      return;
    }

    // Guardar referencia local para evitar errores de null
    const initialData = this.initialData;

    console.log('🔄 Parseando fechas...');
    console.log('🔄 check_in_date string:', initialData.check_in_date);

    // Convertir fechas string a Date objects usando parseLocalDate para evitar bugs de timezone
    const checkInDate = this.parseLocalDate(initialData.check_in_date);
    const checkOutDate = this.parseLocalDate(initialData.check_out_date);

    console.log('🔄 checkInDate parseado:', checkInDate);
    console.log('🔄 checkOutDate parseado:', checkOutDate);

    console.log('📅 Cargando datos iniciales del formulario:', {
      room: initialData.room,
      checkIn: initialData.check_in_date,
      checkOut: initialData.check_out_date,
      checkInDateObject: checkInDate,
      checkOutDateObject: checkOutDate,
      program_id: initialData.program_id,
      status: initialData.status,
    });

    // Guardar el status y fecha inicial en modo edición
    if (this.isEditMode) {
      this.initialStatus = initialData.status;
      this.statusHasChanged = false;
      this.initialCheckInDate = initialData.check_in_date;
      this.checkInDateHasChanged = false;
      console.log('✏️ Datos iniciales guardados:', {
        status: this.initialStatus,
        checkInDate: this.initialCheckInDate,
      });
    }

    // Primero cargar los campos básicos
    console.log('🔄 Ejecutando patchValue con check_in_date:', checkInDate);
    this.reservationForm.patchValue({
      room: initialData.room,
      patient: initialData.patient,
      check_in_date: checkInDate,
      check_out_date: checkOutDate,
      status: initialData.status,
      total_amount: parseFloat(initialData.total_amount || '0'),
      special_requests: initialData.special_requests || '',
    });
    console.log(
      '✅ patchValue ejecutado. Valor actual del formulario:',
      this.reservationForm.value,
    );
    console.log(
      '✅ check_in_date en formulario:',
      this.reservationForm.get('check_in_date')?.value,
    );

    // Forzar detección de cambios para que p-calendar muestre el valor
    this.cdr.detectChanges();

    // Si hay program_id, seleccionarlo
    if (initialData.program_id) {
      console.log('📦 Pre-seleccionando programa:', initialData.program_id);

      // Esperar a que los programas estén cargados
      const checkPrograms = setInterval(() => {
        if (this.programs.length > 0) {
          clearInterval(checkPrograms);

          // Seleccionar el programa
          this.reservationForm.patchValue({
            program: initialData.program_id,
          });

          // Disparar manualmente el evento de cambio de programa
          this.selectedProgram =
            this.programs.find((p) => p.program_id === initialData.program_id) || null;

          if (this.selectedProgram) {
            // Construir planes disponibles
            const plansMap = new Map<number, { prices: ProgramTierPrice[]; tier: string }>();

            this.selectedProgram.tiers.forEach((tier) => {
              if (tier.is_active) {
                tier.prices.forEach((price) => {
                  if (price.is_active) {
                    if (!plansMap.has(price.duration_days)) {
                      plansMap.set(price.duration_days, { prices: [], tier: tier.tier });
                    }
                    const groupedPlan = plansMap.get(price.duration_days);
                    if (groupedPlan) {
                      groupedPlan.prices.push(price);
                    }
                  }
                });
              }
            });

            this.availablePlans = Array.from(plansMap.entries())
              .map(([duration_days, data]) => ({
                duration_days,
                prices: data.prices,
                tier: data.tier,
              }))
              .sort((a, b) => a.duration_days - b.duration_days);

            console.log('📋 Planes disponibles cargados:', this.availablePlans);

            // Calcular los días del paquete desde las fechas
            const checkInDate = this.parseLocalDate(initialData.check_in_date);
            const checkOutDate = this.parseLocalDate(initialData.check_out_date);
            const daysDiff = Math.ceil(
              (checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24),
            );

            console.log('📅 Días calculados del paquete:', daysDiff);

            // Seleccionar el plan correspondiente
            const planToSelect = this.availablePlans.find((p) => p.duration_days === daysDiff);

            if (planToSelect) {
              console.log('✅ Pre-seleccionando plan:', planToSelect);
              this.reservationForm.patchValue({
                plan: `${planToSelect.duration_days}`,
              });

              // Seleccionar el plan manualmente
              this.selectedPlan = planToSelect;
              this.selectedPrice = planToSelect.prices[0]; // Tomar el primer precio
            } else {
              console.warn('⚠️ No se encontró un plan que coincida con', daysDiff, 'días');
            }
          }
        }
      }, 100);

      // Timeout de seguridad
      setTimeout(() => clearInterval(checkPrograms), 5000);
    }
  }

  calculateCheckOutDate(): void {
    const checkInDate = this.reservationForm.get('check_in_date')?.value;
    const durationDays = this.selectedPrice?.duration_days;

    if (checkInDate && durationDays) {
      const checkOutDate = new Date(checkInDate);
      checkOutDate.setDate(checkOutDate.getDate() + durationDays);
      this.reservationForm.patchValue(
        {
          check_out_date: checkOutDate,
        },
        { emitEvent: false },
      );
    }
  }

  /**
   * Recalcular check_out_date en modo edición manteniendo la duración original
   */
  recalculateCheckOutDateInEditMode(): void {
    const checkInDate = this.reservationForm.get('check_in_date')?.value;
    const currentCheckOutDate = this.reservationForm.get('check_out_date')?.value;

    if (!checkInDate || !this.initialCheckInDate || !currentCheckOutDate) {
      return;
    }

    // Calcular la duración original en días
    const originalCheckInDate = new Date(this.initialCheckInDate);
    const originalCheckOutDate = new Date(currentCheckOutDate);
    const durationDays = Math.ceil(
      (originalCheckOutDate.getTime() - originalCheckInDate.getTime()) / (1000 * 60 * 60 * 24),
    );

    // Calcular nueva fecha de check-out manteniendo la misma duración
    const newCheckOutDate = new Date(checkInDate);
    newCheckOutDate.setDate(newCheckOutDate.getDate() + durationDays);

    this.reservationForm.patchValue(
      {
        check_out_date: newCheckOutDate,
      },
      { emitEvent: false },
    );

    console.log('📅 Check-out date recalculado en modo edición:', {
      duraciónOriginal: durationDays,
      nuevaFechaCheckOut: newCheckOutDate,
    });
  }

  getTreatmentDay(): number | null {
    const durationDays = this.selectedPrice?.duration_days;
    return durationDays ? this.treatmentDayMap[durationDays] || null : null;
  }

  /**
   * Validar disponibilidad de la habitación en las fechas seleccionadas
   */
  async validateRoomAvailability(): Promise<void> {
    console.log('🔵 validateRoomAvailability llamado');

    // Limpiar error previo
    this.availabilityError = null;

    // Obtener valores del formulario
    const roomId = this.reservationForm.get('room')?.value;
    const checkInDate = this.reservationForm.get('check_in_date')?.value;
    const checkOutDate = this.reservationForm.get('check_out_date')?.value;

    console.log('📋 Valores del formulario:', {
      roomId,
      checkInDate,
      checkOutDate,
      hasValidationService: !!this.reservationValidationService,
    });

    // Validar que todos los campos necesarios estén presentes
    if (!roomId || !checkInDate || !checkOutDate) {
      console.log('⏭️ Validación omitida: faltan datos del formulario');
      return;
    }

    if (!this.reservationValidationService) {
      console.warn('⚠️ reservationValidationService no está disponible');
      return;
    }

    // No validar en modo edición (la habitación ya está reservada)
    if (this.isEditMode) {
      console.log('⏭️ Validación omitida: modo edición');
      return;
    }

    // Formatear fechas como YYYY-MM-DD
    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const checkInStr = formatDate(checkInDate);
    const checkOutStr = formatDate(checkOutDate);

    console.log('🔍 Validando disponibilidad para:', { roomId, checkInStr, checkOutStr });

    this.isValidatingAvailability = true;

    try {
      const availability = await this.reservationValidationService.checkAvailability(
        checkInStr,
        checkOutStr,
      );

      console.log('📊 Respuesta completa de disponibilidad:', availability);
      console.log('📊 Tipo de respuesta:', typeof availability);
      console.log('📊 Claves de respuesta:', Object.keys(availability || {}));

      // Verificar si la habitación seleccionada está ocupada en esas fechas
      const occupiedRooms =
        availability?.occupied_rooms || availability?.data?.occupied_rooms || [];

      console.log('🔴 Habitaciones ocupadas:', occupiedRooms);
      console.log('🔍 Habitación a buscar:', roomId);
      console.log('🔍 Tipo de roomId:', typeof roomId);

      // Intentar múltiples formas de comparación
      const isRoomOccupied = occupiedRooms.some((room) => {
        console.log('Comparando:', { room, roomId });

        // Si room es un objeto
        if (typeof room === 'object' && room !== null) {
          return room.room_id === roomId || room.room === roomId;
        }

        // Si room es un string
        return room === roomId;
      });

      console.log('❓ ¿Habitación ocupada?:', isRoomOccupied);

      if (isRoomOccupied) {
        this.availabilityError = this.i18n.instant('reservations.form.errors.roomNotAvailableDate');
        console.warn('⚠️ Habitación ocupada:', roomId);

        // Emitir mensaje inmediatamente
        this.showMessage.emit({
          severity: 'warn',
          summary: this.i18n.instant('reservations.form.errors.roomNotAvailableTitle'),
          detail: this.availabilityError,
        });
      } else {
        console.log('✅ Habitación disponible');
      }
    } catch (error) {
      console.error('❌ Error validando disponibilidad:', error);
    } finally {
      this.isValidatingAvailability = false;
    }
  }

  onSubmit(): void {
    console.log('🔵 onSubmit llamado');
    console.log('📝 Estado del formulario:', {
      valid: this.reservationForm.valid,
      value: this.reservationForm.getRawValue(),
      isEditMode: this.isEditMode,
      statusHasChanged: this.statusHasChanged,
    });
    console.log('🔍 Estado de availabilityError:', this.availabilityError);
    console.log('🔍 Estado de isValidatingAvailability:', this.isValidatingAvailability);

    // En modo edición, validar que el status o la fecha hayan cambiado
    if (this.isEditMode) {
      if (!this.statusHasChanged && !this.checkInDateHasChanged) {
        console.warn('⚠️ En modo edición: no hay cambios en status ni en fecha');
        this.showMessage.emit({
          severity: 'warn',
          summary: this.i18n.instant('reservations.form.messages.noChangesTitle'),
          detail: this.i18n.instant('reservations.form.messages.noChangesDetail'),
        });
        return;
      }
      // En modo edición, no validar otros campos
      console.log('✅ Modo edición: hay cambios, procediendo con actualización', {
        statusCambió: this.statusHasChanged,
        fechaCambió: this.checkInDateHasChanged,
      });
    } else {
      // Modo creación: validaciones normales

      // Bloquear submit si aún se está validando
      if (this.isValidatingAvailability) {
        console.warn('⏳ Validación en progreso, bloqueando submit');
        this.showMessage.emit({
          severity: 'info',
          summary: this.i18n.instant('reservations.form.messages.validatingTitle'),
          detail: this.i18n.instant('reservations.form.messages.validatingDetail'),
        });
        return;
      }

      // Validar error de disponibilidad
      if (this.availabilityError) {
        console.warn('⚠️ Error de disponibilidad detectado:', this.availabilityError);
        this.showMessage.emit({
          severity: 'warn',
          summary: this.i18n.instant('reservations.form.errors.roomNotAvailableTitle'),
          detail: this.availabilityError,
        });
        return;
      }

      if (this.reservationForm.invalid) {
        console.warn('⚠️ Formulario inválido');
        Object.keys(this.reservationForm.controls).forEach((key) => {
          const control = this.reservationForm.get(key);
          control?.markAsTouched();
          if (control?.invalid) {
            console.warn(`❌ Campo inválido: ${key}`, control.errors);
          }
        });
        return;
      }
    }

    const formValue = this.reservationForm.getRawValue();

    // Formatear fechas como YYYY-MM-DD
    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const statusValue = Array.isArray(formValue.status) ? formValue.status[0] : formValue.status;

    const reservationData: ReservationFormData = {
      room: formValue.room,
      patient: formValue.patient, // Patient ID
      check_in_date: formatDate(formValue.check_in_date),
      check_out_date: formatDate(formValue.check_out_date),
      status: statusValue,
      total_amount: String(formValue.total_amount),
      deposit_amount: '0.00',
      special_requests: formValue.special_requests || '',
      program_id: formValue.program,
      tier_id: formValue.tier,
      price_id: formValue.price,
    };

    console.log('📦 ReservationData final:', reservationData);

    // Emitir la reserva
    this.formSubmit.emit({
      reservationData,
      pendingPayment: null,
    });
  }

  onCancel(): void {
    // Devolver habitación a disponible si se había seleccionado una
    this.resetRoomToAvailable();
    this.formCancel.emit();
  }

  /**
   * Resetear completamente el formulario
   * @param keepRoomOccupied Si es true, NO devuelve la habitación a available (usar después de guardar exitosamente)
   * Método público para ser llamado desde el componente padre
   */
  public resetForm(keepRoomOccupied = false): void {
    // Solo devolver habitación a disponible si NO se guardó la reservación
    if (!keepRoomOccupied) {
      this.resetRoomToAvailable();
    } else {
      // Limpiar el previousRoomId sin cambiar el estado de la habitación
      this.previousRoomId = null;
    }

    // Resetear el formulario
    this.reservationForm.reset({
      status: 'tentative',
    });

    // Limpiar variables de estado
    this.selectedPatient = null;
    this.selectedProgram = null;
    this.selectedPlan = null;
    this.selectedPrice = null;
    this.availablePlans = [];
    this.filteredPatients = [];
    this.previousRoomId = null;
    this.availabilityError = null;
    this.isValidatingAvailability = false;
    this.initialStatus = null;
    this.statusHasChanged = false;
    this.initialCheckInDate = null;
    this.checkInDateHasChanged = false;

    console.log('✅ Formulario reseteado completamente');
  }

  /**
   * Devolver la habitación previamente seleccionada a estado "available"
   */
  private resetRoomToAvailable(): void {
    if (this.previousRoomId && !this.isEditMode && this.roomUpdateService) {
      console.log(`🔓 Devolviendo habitación ${this.previousRoomId} a disponible...`);
      this.roomUpdateService.updateRoom(this.previousRoomId, { status: 'available' });
      this.previousRoomId = null;
    }
  }

  /**
   * Precargar datos en el formulario desde la lista de espera u otra fuente
   * @param data Datos a precargar: paciente, fechas y notas
   */
  public preloadData(data: {
    patient: Patient;
    check_in_date?: string;
    check_out_date?: string;
    notes?: string;
  }): void {
    console.log('📥 preloadData llamado con:', data);

    if (!data.patient) {
      console.warn('⚠️ preloadData: No se proporcionó información del paciente');
      return;
    }

    // Setear el paciente seleccionado
    this.selectedPatient = data.patient;
    this.patientSearchControl.setValue(data.patient);

    // Actualizar el formulario con el paciente
    this.reservationForm.patchValue({
      patient: data.patient.patient_id,
    });

    // Setear fechas si están disponibles
    if (data.check_in_date) {
      const checkInDate = this.parseLocalDate(data.check_in_date);
      this.reservationForm.patchValue({
        check_in_date: checkInDate,
      });
      console.log('📅 Fecha de check-in precargada:', checkInDate);
    }

    if (data.check_out_date) {
      const checkOutDate = this.parseLocalDate(data.check_out_date);
      this.reservationForm.patchValue({
        check_out_date: checkOutDate,
      });
      console.log('📅 Fecha de check-out precargada:', checkOutDate);
    }

    // Setear notas/solicitudes especiales si están disponibles
    if (data.notes) {
      this.reservationForm.patchValue({
        special_requests: data.notes,
      });
      console.log('📝 Notas precargadas:', data.notes);
    }

    // Forzar detección de cambios
    this.cdr.detectChanges();

    console.log('✅ Datos precargados exitosamente');
    console.log('📋 Estado actual del formulario:', this.reservationForm.value);
  }

  get roomOptions() {
    // Mostrar TODAS las habitaciones (no filtrar por disponibilidad)
    // La validación de disponibilidad se hace por fechas
    return this.rooms.map((room) => ({
      label: `${room.room_number}${room.room_type ? ` (${room.room_type})` : ''}`,
      value: room.room_id,
    }));
  }

  /**
   * Calcular días del paquete desde las fechas del formulario
   * Usado en modo vista cuando no hay plan seleccionado
   */
  getPackageDaysFromDates(): number {
    const checkInDate = this.reservationForm.get('check_in_date')?.value;
    const checkOutDate = this.reservationForm.get('check_out_date')?.value;

    if (!checkInDate || !checkOutDate) {
      return 0;
    }

    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);
    const diffTime = checkOut.getTime() - checkIn.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 0 ? diffDays : 0;
  }

  /**
   * Formatear fecha a YYYY-MM-DD
   */
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
